import React, { useState } from 'react';

import { Alert, Popover } from 'antd';
import Axios from 'axios';
import { Formik, Field } from 'formik';

import styled from 'styled-components';

import { showLogNoTime } from '@libs/NwMoment';

import { forceReloadProjectTransactions } from '@hooks/API/Project/UseProjectTransactions';
import { forceReloadTransaction } from '@hooks/API/UseTransaction';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import Money from '@components/Gui/Money';
import { NwCancelButton, NwButton } from '@components/Gui/NwButton';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWInputNumber } from '@components/Gui/NwForm/NwFormItems';
import { NwForm } from '@components/Gui/NwForm/NwFormWrapper';
import NwLink from '@components/Gui/NwLink';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const ExchangeFormContainer = styled.div`
    padding-top: 2rem;

    p {
        font-size: 0.85rem;
        font-weight: 400;
        color: ${(props) => props.theme.TextColorMuted};
        text-transform: uppercase;

        svg {
            color: ${(props) => props.theme.TextColor};
        }
    }

    .form-line {
        display: flex;
        align-items: center;
        justify-content: flex-start;

        span {
            padding: 0 0.5rem 0.75rem 0.5rem;
        }
    }
`;

const ExchangeRateForm = ({ statement, transaction, asCurrency, disabled, onUpdate }) => {
    const [editable, setEditable] = useState(false);
    const [touchForm, setTouchForm] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const configuration = useConfigurations();

    let initialValues = {
        ExchangeRate: statement && statement.ExchangeRate && statement.ExchangeRate > 0 ? statement.ExchangeRate : 1,
    };

    const callApi = async (exchangeRate) => {
        setErrorMessage(null);
        if (statement) {
            try {
                const data = {
                    StatementID: statement.ID,
                    ExchangeRate: exchangeRate,
                    TransactionID: transaction.ID,
                };
                await Axios.put(`/transactions/${transaction.Family}/modifystatementexchangerate`, data);
                forceReloadTransaction(transaction.ID);
                if (transaction.ProjectID) {
                    forceReloadProjectTransactions(transaction.ProjectID);
                }
                setTouchForm(false);
                setEditable(false);
                if (onUpdate) {
                    onUpdate();
                }
            } catch (error) {
                setErrorMessage(`Error changing exchange rate: ${error?.message || 'unknown error'}`);
            }
        } else {
            try {
                const data = {
                    ExchangeRate: exchangeRate,
                    TransactionID: transaction.ID,
                };
                await Axios.put(`/transactions/${transaction.Family}/modifyexchangerate`, data);
                forceReloadTransaction(transaction.ID);
                if (transaction.ProjectID) {
                    forceReloadProjectTransactions(transaction.ProjectID);
                }
                setTouchForm(false);
                setEditable(false);
                if (onUpdate) {
                    onUpdate();
                }
            } catch (error) {
                setErrorMessage(`Error changing exchange rate: ${error?.message || 'unknown error'}`);
            }
        }
    };

    const submitForm = async (values, setSubmitting) => {
        setSubmitting(true);
        await callApi(values.ExchangeRate);
        setSubmitting(false);
    };

    const clearConversion = async () => {
        await callApi(0);
    };

    const FormTrigger = () => {
        if (!statement) {
            return (
                <NwLink
                    disabled={disabled}
                    onClick={() => {
                        setEditable(true);
                    }}
                    icon={byPrefixAndName.fasl['money-bill-transfer']}
                >
                    Set Statements Exchange Rate
                </NwLink>
            );
        }
        if (asCurrency) {
            return statement && statement.ExchangeRate > 0 ? (
                <Popover
                    zIndex={9999}
                    placement="left"
                    trigger="hover"
                    title="Foreign Currency"
                    content={
                        <>
                            <p>
                                <Money redIfNegative amount={statement.Amount} currency={transaction.Currency} />
                            </p>
                            <p>
                                Exchange Rate:&nbsp;{statement.ExchangeRate} [{showLogNoTime(statement.ExchangeDate)}]
                            </p>
                        </>
                    }
                >
                    <NwLink
                        disabled={disabled}
                        onClick={() => {
                            setEditable(true);
                        }}
                    >
                        {configuration.defaultCurrency}
                    </NwLink>
                </Popover>
            ) : (
                <NwLink
                    disabled={disabled}
                    onClick={() => {
                        setEditable(true);
                    }}
                >
                    {transaction.Currency}
                </NwLink>
            );
        }
    };

    return (
        <>
            <FormTrigger />
            {editable && (
                <NwDrawer
                    touchForm={touchForm}
                    onTouchForm={setTouchForm}
                    onClose={() => {
                        setEditable(false);
                        setTouchForm(false);
                        setErrorMessage(null);
                    }}
                    title="Edit Exchange Rate"
                >
                    <ExchangeFormContainer>
                        <p>Exchange rate</p>
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize={true}
                            onSubmit={(values, { setStatus, setSubmitting }) => {
                                setStatus();
                                submitForm(values, setSubmitting);
                            }}
                        >
                            {({ isSubmitting, values, handleSubmit }) => {
                                return (
                                    <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit}>
                                        <div className="form-line">
                                            <span>
                                                1 <strong>{transaction.Currency}</strong> ={' '}
                                            </span>
                                            <Field
                                                component={NWInputNumber}
                                                label=""
                                                name="ExchangeRate"
                                                min={0}
                                                step={0.0001}
                                                value={values.ExchangeRate}
                                                style={{ width: '10rem' }}
                                            />
                                            <span>
                                                {' '}
                                                <strong>{configuration.defaultCurrency}</strong>
                                            </span>
                                            <span>
                                                <NwButton
                                                    htmlType="submit"
                                                    disabled={isSubmitting}
                                                    loading={isSubmitting}
                                                    icon={byPrefixAndName.fass['money-bill-transfer']}
                                                    label="Convert"
                                                />
                                            </span>
                                        </div>
                                        {errorMessage && (
                                            <Alert style={{ margin: '1rem 0' }} message={errorMessage} type="error" />
                                        )}
                                        <br />
                                        <br />
                                        <NwLink onClick={clearConversion}>
                                            Clear Conversion and Switch Back To {transaction.Currency}
                                        </NwLink>
                                        <br />
                                        <br />
                                        <br />
                                        <NwCancelButton
                                            disabled={isSubmitting}
                                            onClick={() => {
                                                setTouchForm(false);
                                                setEditable(false);
                                                setErrorMessage(null);
                                            }}
                                        />
                                        <br />
                                        <br />
                                    </NwForm>
                                );
                            }}
                        </Formik>
                    </ExchangeFormContainer>
                </NwDrawer>
            )}
        </>
    );
};

export default ExchangeRateForm;
